import { ClientFragment } from "../generated/graphql"

type DatedRecord = Record<string, unknown>

function asRecords(value: unknown): DatedRecord[] {
  return Array.isArray(value) ? (value as DatedRecord[]) : []
}

function dateKey(value: unknown): number {
  if (value == null || value === "") {
    return 0
  }
  const ms = Date.parse(String(value))
  return Number.isNaN(ms) ? 0 : ms
}

/** Newest-first by one or more candidate date fields. */
function newestFirst(rows: DatedRecord[], fields: string[]): DatedRecord[] {
  return [...rows].sort((a, b) => {
    const aMax = Math.max(...fields.map((field) => dateKey(a[field])))
    const bMax = Math.max(...fields.map((field) => dateKey(b[field])))
    return bMax - aMax
  })
}

function sanitizeDemographics(
  row: ClientFragment["client_demographics"][number] | undefined
): Record<string, unknown> | null {
  if (!row) {
    return null
  }
  // Whitelist only non-identifying clinical demographics (no names / UUIDs).
  return {
    birth_year: row.birth_year ?? null,
    gender: row.gender ?? null,
    racial_ethnic: row.racial_ethnic ?? null,
  }
}

/**
 * Compact Vira client snapshot for Foundry progress-note generation.
 * Aligned with Promptflow VIRA-3414 fusion intent, without sending names or IDs.
 */
export function buildProgressNoteClientContext(
  client: ClientFragment
): Record<string, unknown> {
  const completedTakes = newestFirst(
    asRecords(client.questionnaire_takes).filter(
      (take) => take.completed_at != null && take.completed_at !== ""
    ),
    ["completed_at", "created_at"]
  ).slice(0, 10)

  return {
    client: {
      demographics: sanitizeDemographics(client.client_demographics?.[0]),
    },
    mood_measurements: newestFirst(asRecords(client.mood_measurements), [
      "client_created_on",
      "created_on",
    ]).slice(0, 14),
    daily_features: newestFirst(asRecords(client.daily_features), [
      "feature_date",
    ]).slice(0, 20),
    activity_rating: newestFirst(asRecords(client.activity_rating), [
      "created_on",
      "client_created_on",
    ]).slice(0, 20),
    questionnaire_takes: completedTakes,
    sleep_features: newestFirst(asRecords(client.vw_daily_features_sleep), [
      "feature_date",
    ]).slice(0, 14),
    trend_features: asRecords(client.vw_daily_features_trend).slice(0, 14),
    growth_reports: asRecords(client.growth_reports).slice(0, 10),
    enjoy_reports: asRecords(client.enjoy_reports).slice(0, 10),
  }
}
